
import { ContentType } from '@rtl/api'
import { mixins, Options, Prop, Watch } from 'vue-property-decorator'

import { CopyHelper, findElement, fixLinkTarget, generateMetaInfo, getPublicUrl, parseFromString, parseHtmlString, stripTags } from '../../utils'
import AdultConfirm from '../components/AdultConfirm.vue'
import MinuteByMinute from '../components/content/MinuteByMinute.vue'
import FacebookFollow from '../components/FacebookFollow.vue'
import Close from '../components/icons/Close.vue'
import Facebook from '../components/icons/Facebook.vue'
import GalleryOutline from '../components/icons/GalleryOutline.vue'
import Link from '../components/icons/Link.vue'
import Twitter from '../components/icons/Twitter.vue'
import Viber from '../components/icons/Viber.vue'
import DynamicScript from '../components/DynamicScript.vue'
import Image from '../components/Image.vue'
import Tag from '../components/Tag.vue'
import TimeAgo from '../components/TimeAgo.vue'
import VideoPlayer from '../components/VideoPlayer.vue'
import PageLayout from '../layouts/PageLayout.vue'
import { FetchContentItemMixin } from '../mixins'
import BannerWidget from '../widgets/BannerWidget.vue'
import ContentListWidget from '../widgets/ContentListWidget.vue'
import ContentWidgetRenderer from '../widgets/ContentWidgetRenderer.vue'
import RecommendedVideoWidget from '../widgets/RecommendedVideoV2Widget.vue'

@Options({
  name: 'StaticPage',
  components: {
    ContentWidgetRenderer,
    PageLayout,
    AdultConfirm,
    BannerWidget,
    ContentListWidget,
    RecommendedVideoWidget,
    Facebook,
    Twitter,
    Viber,
    Image,
    Tag,
    Link,
    TimeAgo,
    VideoPlayer,
    Close,
    MinuteByMinute,
    GalleryOutline,
    FacebookFollow,
    DynamicScript
  },
  head () {
    return generateMetaInfo(this.$route, this.$router, this.page, this.content, this.$store)
  }
})
export default class StaticPage extends mixins(FetchContentItemMixin) {
  @Prop({
    type: Boolean,
    default: false
  })
  readonly enableRecommendedVideos!: boolean

  showRecommendedVideos = false
  isVideoSticky = true
  isShowClose = false
  copyHelper = CopyHelper.create()
  observerHalfTrigger: IntersectionObserver | null = null
  observerContentMutation: MutationObserver | null = null
  observerContentIntersect: IntersectionObserver | null = null
  measuredBlocks: Array<string> = []
  publicUrl!: string

  get isMinuteByMinuteContent (): boolean {
    return this.content?.contentType === ContentType.MinuteByMinute
  }

  get isGallery (): boolean {
    return (this.content?.contentType === ContentType.Gallery || !!this.content?.params?.embedGalleryUrl)
  }

  get parsedBody () {
    if (this.content?.body) {
      const regex = /<script\b[^>]*>[\s\S]*?<\/script\b[^>]*>/g
      const scripts = this.content.body.match(regex) || []
      let html = this.content.body
      const contentScripts: Array<{ attrs: {[key: string]: unknown}, content: string }> = []

      scripts.forEach((script: string) => {
        html = html.replace(script, '')
        const parsed = parseHtmlString(script)
        if (parsed && parsed.length) {
          const attrs = Array.from((parsed[0] as HTMLScriptElement).attributes).reduce((acc: {[key: string]: unknown}, attr) => {
            acc[attr.nodeName] = attr.nodeValue
            return acc
          }, {})
          contentScripts.push({ attrs, content: (parsed[0] as HTMLScriptElement).innerHTML })
        }
      })

      const htmlBody = findElement(parseFromString(`<body>${html}</body>`), 'body')!
      
      return {
        body: htmlBody,
        scripts: contentScripts
      }
    }

    return null
  }

  get contentBody () {
    /* Dynamically add banner zones to content body:
     * Place               | Desktop       | Mobile                 | App                      | Tablet
     * After 02. paragraph | 'teads'       | 'm_teads'              | 'app_teads'              | 't_teads'
     * After 05. paragraph | 'roadblock_2' | 'm_medium_rectangle_1' | 'app_medium_rectangle_1' | null
     * After 08. paragpaph | 'roadblock_3' | 'm_medium_rectangle_2' | 'app_medium_rectangle_2' | null
     * After 11. paragraph | 'roadblock_4' | 'm_medium_rectangle_3' | 'app_medium_rectangle_3' | null
     * After 14. paragraph | 'roadblock_5' | 'm_medium_rectangle_4' | 'app_medium_rectangle_4' | null
     * After 17. paragraph | 'roadblock_6' | null                   | null                     | null
     */

    const chunkFrom = 5 // Count of elements before the first banner
    const chunkSize = 3 // Count of elements between banners
    const parsed = this.parsedBody

    if (parsed) {
      const bodyEl = parsed.body
      const nodes = Array.from(bodyEl.childNodes)

      const chunks = nodes.reduce<Array<HTMLElement>>((chunks, node) => {
        if (chunks[chunks.length - 1]?.children.length === (chunks.length === 1 ? chunkFrom : chunkSize)) {
          const index = chunks.filter(el => el.tagName.toLowerCase() === 'banner').length
          const desktopZone = index < 5 ? `'roadblock_${index + 2}'` : null
          const mobileZone = index < 4 ? `'m_medium_rectangle_${index + 1}'` : null
          const appZone = index < 4 ? `'app_medium_rectangle_${index + 1}'` : null
          const tabletZone = null

          if (desktopZone || mobileZone || appZone || tabletZone) {
            const bannerEl = bodyEl.ownerDocument.createElement('banner')
            bannerEl.setAttribute(':zone', `{ desktop: ${desktopZone}, tablet: ${tabletZone}, mobile: ${mobileZone}, app: ${appZone} }`)
            bannerEl.classList.add('static-page__banner', 'static-page__banner--roadblock')
            chunks.push(bannerEl)
          }
        }

        if (chunks[chunks.length - 1]?.tagName.toLowerCase() !== 'div') {
          const chunkEl = bodyEl.ownerDocument.createElement('div')
          chunkEl.classList.add('static-page__content', 'static-page__content--body')
          chunks.push(chunkEl)
        }

        chunks[chunks.length - 1].append(node)

        return chunks
      }, [])

      if (chunks[0].childNodes.length >= 3) {
        const bannerEl = bodyEl.ownerDocument.createElement('banner')
        bannerEl.setAttribute(':zone', `{ desktop: 'teads', tablet: 't_teads', mobile: 'm_teads', app: 'app_teads' }`)
        bannerEl.setAttribute(':show-background', 'false')
        bannerEl.setAttribute('label-position', '')
        bannerEl.classList.add('static-page__banner', 'static-page__banner--teads')
        chunks[0].insertBefore(bannerEl, chunks[0].childNodes[2])
      }

      // Half trigger for recommended videos popup
      const halfTrigger = bodyEl.ownerDocument.createElement('div')
      halfTrigger.classList.add('static-page__half-trigger')
      // chunks.splice(Math.ceil(chunks.length / 2), 0, halfTrigger) // at half
      chunks.unshift(halfTrigger) // at first

      bodyEl.replaceChildren(...chunks)

      return {
        html: bodyEl.innerHTML,
        scripts: parsed.scripts
      }
    }

    return null
  }

  get paragraphsCount () {
    return this.parsedBody?.body.childNodes.length ?? 0
  }

  measureLink (e: Event) {
    if (e.target && (e.target as HTMLAnchorElement).tagName === 'A' && (e.target as HTMLAnchorElement).href) {
      if (this.$gtm && this.$gtm.enabled()) {
        this.$gtm.trackEvent({
          event: 'interaction',
          event_name: 'content_link_click',
          link_url: (e.target as HTMLAnchorElement).href,
          link_text: (e.target as HTMLAnchorElement).innerHTML
        })
      }
    }
  }

  measureTag (tagName: string) {
    if (this.$gtm && this.$gtm.enabled()) {
      this.$gtm.trackEvent({
        event: 'interaction',
        event_name: 'tag_click',
        tag_name: tagName,
        tag_type: 'cimke'
      })
    }
  }

  measureShare (social: string) {
    if (this.$gtm && this.$gtm.enabled()) {
      this.$gtm.trackEvent({
        event: 'interaction',
        event_name: 'share',
        content_type: 'article',
        method: social.charAt(0).toUpperCase() + social.slice(1),
        item_id: this.content?.id
      })
    }
  }

  measureRecommendation (type: string, name: string, target?: { id: string, position: number }) {
    if (this.$gtm && this.$gtm.enabled()) {
      this.$gtm.trackEvent({
        event: 'interaction',
        event_name: type,
        referral_name: name,
        ...target ? {
          recommendation_name: target.id,
          item_index: target.position
        } : null
      })
    }
  }

  videoWrapperCheck () {
    const wrapper = document.querySelector('.static-page__featured-video-wrapper')
    const top = wrapper?.getBoundingClientRect().top || null
    const minTop = 55
    if (top !== null) {
      this.isShowClose = Math.round(top) <= minTop
    }
  }

  @Watch('content.params.embedVideoUrl', { immediate: true })
  initStickyScroll () {
    if (typeof window !== 'undefined' && this.content?.params?.embedVideoUrl) {
      document.addEventListener('scroll', this.videoWrapperCheck)
    }
  }
  
  @Watch('content', { immediate: true })
  initHalfTrigger () {
    this.showRecommendedVideos = false
    this.observerHalfTrigger?.disconnect()

    if (typeof window !== 'undefined' && this.content) {
      this.$nextTick(() => {
        this.observerHalfTrigger = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.showRecommendedVideos = true
              this.observerHalfTrigger?.disconnect()
            }
          })
        }, { threshold: 1.0 })

        const halfTrigger = document.querySelector('.static-page__half-trigger')

        if (halfTrigger) {
          this.observerHalfTrigger.observe(halfTrigger)
        }
      })
    }
  }
  
  @Watch('content', { immediate: true })
  initContentBlock () {
    this.observerContentMutation?.disconnect()
    this.observerContentIntersect?.disconnect()

    if (typeof window !== 'undefined' && this.content) {
      this.$nextTick(() => {
        this.observerContentIntersect = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const block = (entry.target as HTMLElement).dataset.contentBlock
            if (block && entry.isIntersecting) {
              if (!this.measuredBlocks.includes(block)) {
                this.measuredBlocks.push(block)
                this.measureRecommendation('view_recommendation', block)
              }
              this.observerContentIntersect?.unobserve(entry.target)
            }
          })
        }, { threshold: 0 })

        Array.from<Element>(window.document.querySelectorAll('[data-content-block]')).forEach(block => {
          this.observerContentIntersect?.observe(block)
        })

        this.observerContentMutation = new MutationObserver((mutationList) => {
          mutationList.forEach(mutation => {
            if (mutation.type === 'childList') {
              const target = mutation.target as HTMLElement
              if (target.dataset?.contentBlock) {
                this.observerContentIntersect?.observe(target)
              }
            }
          })
        })
        this.observerContentMutation?.observe(this.$el.parentElement, { subtree: true, childList: true })
      })
    }
  }

  copyLink () {
    this.copyHelper.copy(this.publicUrl)
    this.measureShare('Link masolas')
  }

  @Watch('$route.fullPath', { immediate: true })
  refreshPageData () {
    this.publicUrl = getPublicUrl()
    this.measuredBlocks = []
  }

  fixLinkTarget (src: string) {
    // Removing target="_blank" from links, because it can break mobile app navigation.
    return fixLinkTarget(src)
  }

  stripTags (text: string | undefined): string | undefined {
    return stripTags(text)
  }

  galleryClick (): void {
    this.$router.push(this.content?.params?.embedGalleryUrl)
  }

  get clickHandler () {
    return (event: MouseEvent) => {
      const block = (event.target as HTMLElement | null)?.closest('[data-content-block]')
      const target = (event.target as HTMLElement | null)?.closest('[data-content-id]')

      if (block instanceof HTMLElement && target instanceof HTMLElement) {
        const id = target.dataset.contentId
        const blockName = block.dataset.contentBlock
        if (id && blockName) {
          if (id === 'show_all') {
            this.measureRecommendation(id, blockName)
          } else {
            const i = Array.from(block.querySelectorAll('[data-content-id]')).indexOf(target)
            this.measureRecommendation('select_recommendation', blockName, { id, position: i + 1 })
          }
        }
      }
    }
  }

  get messageHandler () {
    return (event: MessageEvent) => {
      const heightKey = 'height:'
      const linkKey = 'link:'
      try {
        const data = typeof event.data === 'object' ? event.data : { action: event.data }
        document.querySelectorAll('iframe').forEach((frame: HTMLIFrameElement) => {
          if (frame.contentWindow === event.source) {
            if (data.action?.startsWith(heightKey)) {
              const height = data.action.substring(heightKey.length)
              frame.style.height = height + 'px'
            } else if (data.action?.startsWith(linkKey)) {
              this.$router.push(data.action.substring(linkKey.length))
            }
          }
        })
      } catch (_e) {}
    }
  }

  async mounted () {
    if (typeof window !== 'undefined') {
      window.addEventListener('click', this.clickHandler, { capture: true })
      window.addEventListener('message', this.messageHandler, false)
    }
  }
  
  beforeUnmount () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('click', this.clickHandler, { capture: true })
      window.removeEventListener('message', this.messageHandler, false)
    }
  }

  unmounted () {
    if (this.content?.params?.embedVideoUrl) {
      document.removeEventListener('scroll', this.videoWrapperCheck)
    }
    this.observerHalfTrigger?.disconnect()
    this.observerContentMutation?.disconnect()
    this.observerContentIntersect?.disconnect()
  }
}
